import {
  ProductTaxInheritedValue,
  ProductTaxMode,
  ProductTaxOption,
  ProductTaxSource,
  ProductTaxSubmitValue,
  ProductTaxValue,
} from './product-tax.model';

export interface ProductTaxCategoryDisplayInput {
  taxCode?: string | null;
  taxName?: string | null;
}

export const DEFAULT_PRODUCT_TAX_VALUE: ProductTaxValue = {
  mode: 'INHERIT',
  taxId: null,
  isPriceIncludeVAT: null,
};

export function normalizeProductTaxSubmit(
  value: ProductTaxValue,
): ProductTaxSubmitValue {
  if (value.mode !== 'OVERRIDE') {
    return {
      taxId: null,
      isPriceIncludeVAT: null,
    };
  }

  return {
    taxId: normalizeOptionalNumber(value.taxId),
    isPriceIncludeVAT: value.isPriceIncludeVAT === true,
  };
}

export function resolveProductTaxDisplayValue(
  value: ProductTaxValue,
  inherited: ProductTaxInheritedValue | null,
): ProductTaxValue {
  if (value.mode === 'OVERRIDE') {
    return {
      mode: 'OVERRIDE',
      taxId: normalizeOptionalNumber(value.taxId),
      isPriceIncludeVAT: value.isPriceIncludeVAT === true,
    };
  }

  return {
    mode: 'INHERIT',
    taxId: inherited?.taxId ?? null,
    isPriceIncludeVAT: inherited?.isPriceIncludeVAT ?? null,
  };
}

export function deriveProductTaxMode(
  taxId?: number | null,
  isPriceIncludeVAT?: boolean | null,
): ProductTaxMode {
  return taxId != null || isPriceIncludeVAT != null ? 'OVERRIDE' : 'INHERIT';
}

export function normalizeProductTaxValue(
  value?: Partial<ProductTaxValue> | null,
): ProductTaxValue {
  const mode = value?.mode ?? 'INHERIT';
  if (mode === 'OVERRIDE') {
    return {
      mode,
      taxId: normalizeOptionalNumber(value?.taxId),
      isPriceIncludeVAT: value?.isPriceIncludeVAT === true,
    };
  }

  return DEFAULT_PRODUCT_TAX_VALUE;
}

export function findProductTaxOption(
  options: ProductTaxOption[],
  taxId?: number | null,
): ProductTaxOption | null {
  const normalizedTaxId = normalizeOptionalNumber(taxId);
  if (normalizedTaxId === null) {
    return null;
  }

  return options.find((option) => option.value === normalizedTaxId) ?? null;
}

export function formatProductTaxCategoryDisplay(
  value?: ProductTaxCategoryDisplayInput | null,
  fallback = '-',
): string {
  const taxCode = value?.taxCode?.trim();
  const taxName = value?.taxName?.trim();

  if (taxCode && taxName) {
    return `${taxCode} - ${taxName}`;
  }
  if (taxCode) {
    return taxCode;
  }
  if (taxName) {
    return taxName;
  }
  return fallback;
}

export function normalizeProductTaxSource(
  source?: string | null,
): ProductTaxSource {
  switch (source) {
    case 'SKU_OVERRIDE':
    case 'SPU_OVERRIDE':
    case 'CATEGORY':
    case 'SYSTEM_DEFAULT':
    case 'UNCONFIGURED':
      return source;
    default:
      return 'UNCONFIGURED';
  }
}

export function normalizeOptionalNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numberValue = Number(value);
  return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : null;
}
